import { useState } from 'react';
import { CheckCircle, Edit2, Trash2, Plus, Lock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { YourCheckaNavigation } from '@/components/Navbar';
import EditDetailsModal from '@/components/modals/EditDetailsModal';
import AddBankAccountModal from '@/components/modals/AddBankAccountModal';
import EditBankAccountModal from '@/components/modals/EditBankAccountModal';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { 
  useGetMyBankAccountsQuery, 
  useSetDefaultBankAccountMutation, 
  useDeleteBankAccountMutation
} from '@/store/api/apiSlice';
import type { BankAccount } from '@/types';

type TabType = 'account' | 'security' | 'payment';

export default function Settings() {
  const [activeTab, setActiveTab] = useState<TabType>('account');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddBankModalOpen, setIsAddBankModalOpen] = useState(false);
  const [editingBankAccount, setEditingBankAccount] = useState<BankAccount | null>(null);
  const currentUser = useAppSelector(selectCurrentUser);

  // Fetch bank accounts
  const { data: bankAccounts = [], isLoading: loadingBankAccounts } = useGetMyBankAccountsQuery();
  const [setDefaultBankAccount] = useSetDefaultBankAccountMutation();
  const [deleteBankAccount] = useDeleteBankAccountMutation();

  const handleSetDefault = async (id: string) => {
    try {
      await setDefaultBankAccount(id).unwrap();
    } catch (error) {
      console.error('Failed to set default account:', error);
    }
  };

  const handleDeleteAccount = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this bank account?')) {
      try {
        await deleteBankAccount(id).unwrap();
      } catch (error) {
        console.error('Failed to delete account:', error);
      }
    }
  };

  // Get user initials
  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  };

  // Format status
  const formatStatus = (status: string) => {
    return status.charAt(0) + status.slice(1).toLowerCase();
  };

  if (!currentUser) {
    return (
      <main className="w-full min-h-screen lg:h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
        <div className="bg-white rounded-3xl shadow-sm flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="w-full min-h-screen lg:h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
      <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
        {/* Header/Navbar */}
        <YourCheckaNavigation
          showTitle={false}
          showInfo={false}
          showLocation={false}
          notificationCount={3}
          userName={`${currentUser.firstName} ${currentUser.lastName}`}
          userInitials={getInitials(currentUser.firstName, currentUser.lastName)}
          only={true}
          className="border-b border-gray-100"
        />

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 md:p-8 lg:p-10">
          {/* Title */}
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-6 md:mb-8 text-start">
            Settings
          </h1>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 md:gap-3 mb-6 md:mb-8">
            <button
              onClick={() => setActiveTab('account')}
              className={`px-6 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                activeTab === 'account'
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              Account Settings
            </button>
            <button
              onClick={() => setActiveTab('security')}
              className={`px-6 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                activeTab === 'security'
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              Security
            </button>
            <button
              onClick={() => setActiveTab('payment')}
              className={`px-6 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                activeTab === 'payment'
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              Payment Details
            </button>
          </div>

          {/* Account Settings Content */}
          {activeTab === 'account' && (
            <div>
              {/* Section Title */}
              <h2 className="text-xl font-bold text-gray-900 mb-6 text-start">Personal Details</h2>

              {/* Personal Details Card */}
              <div className="flex flex-col sm:flex-row items-start gap-4 md:gap-6">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-[#FF6B35] flex items-center justify-center">
                    <span className="text-white text-xl font-semibold">
                      {getInitials(currentUser.firstName, currentUser.lastName)}
                    </span>
                  </div>
                </div>

                {/* User Details */}
                <div className="flex-1 space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-2">
                    <span className="text-sm text-gray-600 sm:min-w-[110px]">Fullname:</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {currentUser.firstName} {currentUser.lastName}
                    </span>
                  </div>

                  {currentUser.gender && (
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-gray-600 min-w-[110px]">Gender:</span>
                      <span className="text-sm font-semibold text-gray-900 capitalize">
                        {currentUser.gender}
                      </span>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-2">
                    <span className="text-sm text-gray-600 sm:min-w-[110px]">Email:</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {currentUser.email}
                    </span>
                  </div>

                  {currentUser.phoneNumber && (
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-gray-600 min-w-[110px]">Phone number:</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {currentUser.phoneNumber}
                      </span>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-2">
                    <span className="text-sm text-gray-600 sm:min-w-[110px]">User Type:</span>
                    <span className="text-sm font-semibold text-gray-900 capitalize">
                      {currentUser.role.replace('_', ' ')}
                    </span>
                  </div>

                  {currentUser.state && (
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-gray-600 min-w-[110px]">State:</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {currentUser.state}
                      </span>
                    </div>
                  )}

                  {currentUser.country && (
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-gray-600 min-w-[110px]">Country:</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {currentUser.country}
                      </span>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-2">
                    <span className="text-sm text-gray-600 sm:min-w-[110px]">Status:</span>
                    <Badge
                      variant="outline"
                      className={`${
                        currentUser.status === 'ACTIVE'
                          ? 'bg-green-50 text-green-700 border-green-100'
                          : currentUser.status === 'INACTIVE'
                          ? 'bg-gray-50 text-gray-700 border-gray-100'
                          : 'bg-red-50 text-red-700 border-red-100'
                      } px-3 py-0.5 rounded-lg font-medium text-xs`}
                    >
                      {formatStatus(currentUser.status)}
                    </Badge>
                  </div>
                </div>

                {/* Edit Button */}
                <div className="flex-shrink-0">
                  <Button
                    onClick={() => setIsEditModalOpen(true)}
                    variant="outline"
                    className="px-6 py-2 border-[#FF6B35] text-[#FF6B35] hover:bg-[#FF6B35] hover:text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Edit Details
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Security Content */}
          {activeTab === 'security' && (
            <div className="space-y-6 md:space-y-8">
              {/* Two-Factor Authentication Section */}
              <div className="border border-gray-200 rounded-xl p-4 md:p-6">
                <div className="flex flex-col sm:flex-row items-start gap-4 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Lock className="w-6 h-6 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-bold text-gray-900">
                        Two-steps verification
                      </h3>
                      <Badge
                        variant="outline"
                        className="bg-green-50 text-green-700 border-green-100 px-3 py-0.5 rounded-lg font-medium text-xs flex items-center gap-1"
                      >
                        <CheckCircle className="w-3 h-3" />
                        Verified
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 text-start">
                      Keep your account secure with authentication step.
                    </p>
                  </div>
                </div>

                {/* SMS Section */}
                {currentUser.phoneNumber && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-1">SMS</h4>
                        <p className="text-sm text-gray-600">{currentUser.phoneNumber}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white transition-colors">
                          <Edit2 className="w-4 h-4 text-gray-600" />
                        </button>
                        <button className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white transition-colors">
                          <Trash2 className="w-4 h-4 text-gray-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Description */}
                <p className="text-sm text-gray-600 text-start">
                  Two-factor authentication adds a layer of security to your account by requiring more than just a password to log in.{' '}
                  <a href="#" className="text-gray-900 font-medium underline">
                    Learn more
                  </a>
                  .
                </p>
              </div>
            </div>
          )}

          {/* Payment Details Content */}
          {activeTab === 'payment' && (
            <div>
              {/* Header with Add Bank button */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Payment methods</h2>
                <Button 
                  onClick={() => setIsAddBankModalOpen(true)}
                  className="bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white px-6 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Bank
                </Button>
              </div>

              {/* Bank Accounts List */}
              {loadingBankAccounts ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading bank accounts...</p>
                </div>
              ) : bankAccounts.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
                  <p className="text-gray-500 mb-4">No bank accounts added yet</p>
                  <Button 
                    onClick={() => setIsAddBankModalOpen(true)}
                    className="bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white px-6 py-2.5 rounded-xl text-sm font-medium"
                  >
                    Add Your First Bank Account
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {bankAccounts.map((account) => (
                  <div
                    key={account.id}
                    className="border border-gray-200 rounded-xl p-4 md:p-6"
                  >
                    <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                      {/* Bank Info */}
                      <div className="flex items-start gap-4">
                        {/* Mastercard Icon */}
                        <div className="w-12 h-12 flex-shrink-0 hidden sm:block">
                          <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="18" cy="24" r="12" fill="#EB001B"/>
                            <circle cx="30" cy="24" r="12" fill="#F79E1B"/>
                            <path d="M24 14.4C21.6 16.4 20 19.2 20 22.4C20 25.6 21.6 28.4 24 30.4C26.4 28.4 28 25.6 28 22.4C28 19.2 26.4 16.4 24 14.4Z" fill="#FF5F00"/>
                          </svg>
                        </div>

                        {/* Account Details */}
                        <div className="space-y-1">
                          <p className="text-sm text-gray-600">
                            Bank Name: <span className="font-semibold text-gray-900">{account.bankName}</span>
                          </p>
                          <p className="text-sm text-gray-600">
                            Account Number: <span className="font-semibold text-gray-900">{account.accountNumber}</span>
                          </p>
                          <p className="text-sm text-gray-600">
                            Account Name: <span className="font-semibold text-gray-900">{account.accountName}</span>
                          </p>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                        {!account.isDefault && (
                          <Button
                            onClick={() => handleSetDefault(account.id)}
                            variant="outline"
                            className="px-4 py-2 rounded-lg text-sm font-medium border-[#FF6B35] text-[#FF6B35] hover:bg-[#FF6B35] hover:text-white"
                          >
                            Set As Default
                          </Button>
                        )}
                        {account.isDefault && (
                          <Badge className="bg-green-50 text-green-700 border-green-100 px-3 py-1">
                            Default
                          </Badge>
                        )}
                        <Button
                          onClick={() => setEditingBankAccount(account)}
                          variant="outline"
                          className="px-4 py-2 bg-gray-900 text-white hover:bg-gray-800 rounded-lg text-sm font-medium border-0"
                        >
                          Edit
                        </Button>
                        <Button
                          onClick={() => handleDeleteAccount(account.id)}
                          variant="outline"
                          className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg text-sm font-medium border-0"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit Details Modal */}
      <EditDetailsModal 
        isOpen={isEditModalOpen} 
        onClose={() => setIsEditModalOpen(false)} 
      />

      {/* Add Bank Account Modal */}
      <AddBankAccountModal
        isOpen={isAddBankModalOpen}
        onClose={() => setIsAddBankModalOpen(false)}
      />

      {/* Edit Bank Account Modal */}
      <EditBankAccountModal
        isOpen={!!editingBankAccount}
        onClose={() => setEditingBankAccount(null)}
        bankAccount={editingBankAccount}
      />
    </main>
  );
}
